// ============================================================================
// Admin → Users management (/admin/users)
//
// Authority-gated triage view for user_profiles. The JWT bridge
// auto-provisions every authenticated TCS employee into user_profiles
// (status='pending', role='pending') on first sign-in. This module is
// where an admin assigns them a real role and flips status to 'active',
// disables old accounts, and edits notes / re-links employee records.
//
// Access: has_full_am_authority — superuser, executive, national_am.
// RLS already enforces SELECT/UPDATE rules on user_profiles for those
// roles via the rewritten 2026-05-10 policies; the UI guard below is a
// friendlier short-circuit for everyone else.
//
// RPC signatures (verified against pg_proc):
//   provision_user(p_target_id uuid, p_role text,
//                  p_status text DEFAULT 'active',
//                  p_employee_id text DEFAULT NULL,
//                  p_notes text DEFAULT NULL) RETURNS user_profiles
//   disable_user(p_target_id uuid, p_reason text DEFAULT NULL)
//                  RETURNS user_profiles
//
// Approve a pending user      → provision_user(uuid, role, 'active')
// Re-enable a disabled user   → provision_user(uuid, role, 'active')
// Change role on active user  → provision_user(uuid, role, 'active')
// Disable an active user      → disable_user(uuid, reason)
// Edit notes (any user)       → UPDATE user_profiles SET notes (RLS-gated)
// ============================================================================

import { ASSIGNABLE_ROLES, FIRST_ROLE_FOR_A_NEW_SIGN_IN, PENDING_ROLE, canManageUsers, hasFullAmAuthority, isPendingSeat } from '../../shared/role-gates';
import { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  ShieldOff,
  UserCheck,
} from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { useAuth } from '../../shared/auth';
import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
} from '../../shared/components';
import type { UserRole, UserStatus } from '../../shared/types';
import { errorMessage } from '../../shared/errors';

type UserRow = {
  id: string;
  employee_id: string | null;
  role: UserRole;
  status: UserStatus;
  display_name: string | null;
  email: string | null;
  firebase_uid: string | null;
  last_login_at: string | null;
  notes: string | null;
  updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
};

// Roles a target user can be assigned: ASSIGNABLE_ROLES (shared/role-gates).

const STATUS_FILTERS: Array<UserStatus | 'all'> = [
  'all',
  'pending',
  'active',
  'disabled',
];

const ROLE_FILTERS: Array<UserRole | 'all'> = ['all', PENDING_ROLE, ...ASSIGNABLE_ROLES];


function StatusPill({ value }: { value: UserStatus }) {
  const cls =
    value === 'active'
      ? 'bg-st-ok-bg text-st-ok-fg-strong ring-st-ok-border'
      : value === 'pending'
        ? 'bg-st-warn-bg text-st-warn-fg-strong ring-st-warn-border'
        : 'bg-st-danger-bg text-st-danger-fg-strong ring-st-danger-border';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${cls}`}
    >
      {value}
    </span>
  );
}

function RolePill({ value }: { value: UserRole }) {
  const isAdmin = hasFullAmAuthority(value);
  const isPending = isPendingSeat(value);
  const cls = isPending
    ? 'bg-st-warn-bg text-st-warn-fg-strong ring-st-warn-border'
    : isAdmin
      ? 'bg-tcs-inverse text-tcs-inverse-ink ring-tcs-text'
      : 'bg-tcs-bg-muted text-tcs-text-secondary ring-tcs-border';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${cls}`}
    >
      {value}
    </span>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AdminUsers() {
  const { profile } = useAuth();
  const [rows, setRows] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .from('user_profiles')
        .select(
          'id, employee_id, role, status, display_name, email, firebase_uid, last_login_at, notes, updated_by, created_at, updated_at',
        )
        .order('created_at', { ascending: false })
        .limit(500);
      if (fetchErr) throw fetchErr;
      setRows((data ?? []) as UserRow[]);
    } catch (e) {
      console.error('[admin/users] load failed', e);
      setError(errorMessage(e, 'Failed to load users.'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  // Authority gate (mirrors has_full_am_authority).
  if (profile && !canManageUsers(profile)) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <PageHeader title="Users" />
        <EmptyState
          title="Admins only"
          body="The user management view is gated to superusers, executives, and national account managers."
        />
      </div>
    );
  }

  // ----- Mutations --------------------------------------------------------

  async function approveOrChangeRole(id: string, role: UserRole) {
    setBusy(id);
    const prev = rows;
    setRows(rows.map((r) => (r.id === id ? { ...r, role, status: 'active' } : r)));
    const { error: rpcErr } = await supabase.rpc('provision_user', {
      p_target_id: id,
      p_role: role,
      p_status: 'active',
    });
    setBusy(null);
    if (rpcErr) {
      console.error('[admin/users] provision_user failed', rpcErr);
      setRows(prev);
      setError(rpcErr.message);
    } else {
      // Refresh the row from DB for canonical updated_by/updated_at.
      void load();
    }
  }

  async function disable(id: string, reason: string | null) {
    setBusy(id);
    const prev = rows;
    setRows(rows.map((r) => (r.id === id ? { ...r, status: 'disabled' } : r)));
    const { error: rpcErr } = await supabase.rpc('disable_user', {
      p_target_id: id,
      p_reason: reason,
    });
    setBusy(null);
    if (rpcErr) {
      console.error('[admin/users] disable_user failed', rpcErr);
      setRows(prev);
      setError(rpcErr.message);
    } else {
      void load();
    }
  }

  async function saveNotes(id: string, notes: string) {
    setBusy(id);
    const prev = rows;
    setRows(rows.map((r) => (r.id === id ? { ...r, notes } : r)));
    const { error: updateErr } = await supabase
      .from('user_profiles')
      .update({ notes })
      .eq('id', id);
    setBusy(null);
    if (updateErr) {
      console.error('[admin/users] notes update failed', updateErr);
      setRows(prev);
      setError(updateErr.message);
    }
  }

  // ----- Filtering --------------------------------------------------------

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (roleFilter !== 'all' && r.role !== roleFilter) return false;
      if (q) {
        const hay = [r.email, r.display_name, r.employee_id, r.id]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, roleFilter, search]);

  const counts = useMemo(
    () => ({
      pending: rows.filter((r) => r.status === 'pending').length,
      active: rows.filter((r) => r.status === 'active').length,
      disabled: rows.filter((r) => r.status === 'disabled').length,
      total: rows.length,
    }),
    [rows],
  );

  // ----- Loading / error --------------------------------------------------

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <PageHeader title="Users" />
        <div className="mt-4 space-y-2">
          <Skeleton width="100%" height={60} block />
          <Skeleton width="100%" height={60} block />
          <Skeleton width="100%" height={60} block />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <PageHeader title="Users" />
        <ErrorState title="Couldn't load users" body={error} onRetry={load} />
      </div>
    );
  }

  // ----- Render -----------------------------------------------------------

  return (
    <div className="mx-auto max-w-5xl p-6">
      <PageHeader
        title="Users"
        meta={`${counts.pending} pending · ${counts.active} active · ${counts.disabled} disabled · ${counts.total} total`}
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={load}
            icon={<RefreshCw className="h-3.5 w-3.5" />}
          >
            Refresh
          </Button>
        }
      />

      {/* Filters */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
            Status
          </span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border border-tcs-border bg-tcs-field px-2 py-1 text-xs text-tcs-text"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
            Role
          </span>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="rounded border border-tcs-border bg-tcs-field px-2 py-1 text-xs text-tcs-text"
          >
            {ROLE_FILTERS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, employee ID…"
          className="ml-auto w-64 rounded border border-tcs-border bg-tcs-field px-3 py-1 text-xs text-tcs-text placeholder:text-tcs-text-muted"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            title="No users match these filters"
            body="Try clearing the filters or expanding the search."
          />
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-lg border border-tcs-border bg-tcs-raised">
          {filtered.map((row) => (
            <UserRowItem
              key={row.id}
              row={row}
              expanded={expanded === row.id}
              busy={busy === row.id}
              onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
              onApprove={(role) => approveOrChangeRole(row.id, role)}
              onChangeRole={(role) => approveOrChangeRole(row.id, role)}
              onDisable={(reason) => disable(row.id, reason)}
              onSaveNotes={(notes) => saveNotes(row.id, notes)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ----- Row component --------------------------------------------------------

interface UserRowItemProps {
  row: UserRow;
  expanded: boolean;
  busy: boolean;
  onToggle: () => void;
  onApprove: (role: UserRole) => void;
  onChangeRole: (role: UserRole) => void;
  onDisable: (reason: string | null) => void;
  onSaveNotes: (notes: string) => void;
}

function UserRowItem({
  row,
  expanded,
  busy,
  onToggle,
  onApprove,
  onChangeRole,
  onDisable,
  onSaveNotes,
}: UserRowItemProps) {
  // Local form state — initialised from row, reset when the row changes.
  const [pickedRole, setPickedRole] = useState<UserRole>(
    isPendingSeat(row.role) ? FIRST_ROLE_FOR_A_NEW_SIGN_IN : row.role,
  );
  const [reason, setReason] = useState('');
  const [notesDraft, setNotesDraft] = useState(row.notes ?? '');
  const notesDirty = (notesDraft ?? '').trim() !== (row.notes ?? '').trim();

  return (
    <div className="border-b border-tcs-border last:border-b-0">
      {/* Header row */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-tcs-bg-muted"
      >
        <span className="text-tcs-text-muted">
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-tcs-text">
              {row.display_name ?? row.email ?? row.id.slice(0, 8)}
            </span>
            <RolePill value={row.role} />
            <StatusPill value={row.status} />
          </div>
          <div className="mt-0.5 truncate font-mono text-[11px] text-tcs-text-muted">
            {row.email ?? '—'}
            {row.employee_id && (
              <>
                <span className="mx-1">·</span>
                {row.employee_id}
              </>
            )}
          </div>
        </div>
        <div className="shrink-0 text-right text-[11px] text-tcs-text-muted">
          <div>Last login</div>
          <div className="font-mono">{formatDate(row.last_login_at)}</div>
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="space-y-4 border-t border-tcs-border bg-tcs-bg-muted/40 px-4 py-4">
          {/* Status-specific primary action */}
          {row.status === 'pending' && (
            <div className="space-y-2 rounded-md border border-st-warn-border bg-st-warn-bg p-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-st-warn-fg-strong">
                Approve user
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={pickedRole}
                  onChange={(e) => setPickedRole(e.target.value as UserRole)}
                  className="rounded border border-st-warn-border-strong bg-tcs-field px-2 py-1 text-xs"
                >
                  {ASSIGNABLE_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={() => onApprove(pickedRole)}
                  disabled={busy}
                  icon={<CheckCircle2 className="h-3.5 w-3.5" />}
                >
                  Approve with role {pickedRole}
                </Button>
              </div>
              <p className="text-[11px] text-st-warn-fg-strong">
                Calls <code className="font-mono">provision_user</code> — sets
                status to active and assigns the role.
              </p>
            </div>
          )}

          {row.status === 'active' && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-2 rounded-md border border-tcs-border bg-tcs-raised p-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
                  Change role
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={pickedRole}
                    onChange={(e) => setPickedRole(e.target.value as UserRole)}
                    className="rounded border border-tcs-border bg-tcs-field px-2 py-1 text-xs"
                  >
                    {ASSIGNABLE_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onChangeRole(pickedRole)}
                    disabled={busy || pickedRole === row.role}
                    icon={<UserCheck className="h-3.5 w-3.5" />}
                  >
                    Save role
                  </Button>
                </div>
              </div>

              <div className="space-y-2 rounded-md border border-st-danger-border bg-st-danger-bg/50 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-st-danger-fg-strong">
                  Disable user
                </div>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (optional, logged to audit trail)…"
                  className="w-full rounded border border-st-danger-border bg-tcs-field px-2 py-1 text-xs"
                />
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => onDisable(reason.trim() || null)}
                  disabled={busy}
                  icon={<ShieldOff className="h-3.5 w-3.5" />}
                >
                  Disable
                </Button>
              </div>
            </div>
          )}

          {row.status === 'disabled' && (
            <div className="space-y-2 rounded-md border border-tcs-border bg-tcs-raised p-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
                Re-enable user
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={pickedRole}
                  onChange={(e) => setPickedRole(e.target.value as UserRole)}
                  className="rounded border border-tcs-border bg-tcs-field px-2 py-1 text-xs"
                >
                  {ASSIGNABLE_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={() => onApprove(pickedRole)}
                  disabled={busy}
                  icon={<CheckCircle2 className="h-3.5 w-3.5" />}
                >
                  Re-enable with role {pickedRole}
                </Button>
              </div>
              <p className="text-[11px] text-tcs-text-muted">
                Calls <code className="font-mono">provision_user</code> with
                status=active — same path as approving a pending user.
              </p>
            </div>
          )}

          {/* Notes (any status) */}
          <div className="space-y-2 rounded-md border border-tcs-border bg-tcs-raised p-3">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
                Internal notes
              </div>
              {notesDirty && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onSaveNotes(notesDraft.trim())}
                  disabled={busy}
                >
                  Save notes
                </Button>
              )}
            </div>
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              rows={2}
              placeholder="Why this user was given this role, who to ask if there's a problem, etc…"
              className="w-full rounded border border-tcs-border bg-tcs-field px-2 py-1 text-xs"
            />
          </div>

          {/* Metadata footer */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-tcs-text-muted md:grid-cols-3">
            <div>
              <span className="font-semibold">ID:</span>{' '}
              <span className="font-mono">{row.id.slice(0, 8)}…</span>
            </div>
            <div>
              <span className="font-semibold">Firebase UID:</span>{' '}
              <span className="font-mono">
                {row.firebase_uid ? `${row.firebase_uid.slice(0, 10)}…` : '—'}
              </span>
            </div>
            <div>
              <span className="font-semibold">Created:</span>{' '}
              {formatDate(row.created_at)}
            </div>
            <div>
              <span className="font-semibold">Updated:</span>{' '}
              {formatDate(row.updated_at)}
            </div>
            <div>
              <span className="font-semibold">Updated by:</span>{' '}
              <span className="font-mono">
                {row.updated_by ? `${row.updated_by.slice(0, 8)}…` : '—'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
